import { z } from 'zod';

import type {
  CompanyReceiverCounterpartyRef,
  CompanyReceiverType,
  CounterpartySelection,
  CounterpartyRef,
  CounterpartyRefKind,
  CounterpartySnapshot,
  CounterpartySearchSource,
  InternalRcScopeCounterpartyRef,
  PartyAddressSnapshot,
  RegulatoryEntityCounterpartyRef,
  RegulatoryEntityRecord,
  RegulatoryEntityVerificationStatus,
  RegulatoryIdentity,
  RegulatoryIdentityKind,
} from './counterparties.dto.js';
import {
  companyReceiverTypeValues,
  counterpartyRefKindValues,
  counterpartySearchSourceValues,
  regulatoryEntityVerificationStatusValues,
  regulatoryIdentityKindValues,
} from './counterparties.dto.js';
import { RcScopeTypeSchema } from '../shipments/shipments-types.js';

const trimmedStringSchema = z.string().trim().min(1);
const timestampSchema = z.iso.datetime();

export const RegulatoryIdentityKindSchema = z.enum(
  regulatoryIdentityKindValues,
) satisfies z.ZodType<RegulatoryIdentityKind>;

export const RegulatoryIdentitySchema = z
  .object({
    kind: RegulatoryIdentityKindSchema,
    number: trimmedStringSchema,
    normalizedNumber: trimmedStringSchema,
    displayNumber: trimmedStringSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryIdentity>;

export const PartyAddressSnapshotSchema = z
  .object({
    addressLine1: trimmedStringSchema,
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema,
    state: trimmedStringSchema,
    postalCode: trimmedStringSchema,
  })
  .strict() satisfies z.ZodType<PartyAddressSnapshot>;

export const CompanyReceiverTypeSchema = z.enum(
  companyReceiverTypeValues,
) satisfies z.ZodType<CompanyReceiverType>;

export const CounterpartyRefKindSchema = z.enum(
  counterpartyRefKindValues,
) satisfies z.ZodType<CounterpartyRefKind>;

export const InternalRcScopeCounterpartyRefSchema = z
  .object({
    kind: z.literal('INTERNAL_RC_SCOPE'),
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: trimmedStringSchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
  })
  .strict() satisfies z.ZodType<InternalRcScopeCounterpartyRef>;

export const CompanyReceiverCounterpartyRefSchema = z
  .object({
    kind: z.literal('COMPANY_RECEIVER'),
    companyReceiverId: z.uuid(),
    regulatoryEntityId: z.uuid().nullable().optional(),
  })
  .strict() satisfies z.ZodType<CompanyReceiverCounterpartyRef>;

export const RegulatoryEntityCounterpartyRefSchema = z
  .object({
    kind: z.literal('REGULATORY_ENTITY'),
    regulatoryEntityId: z.uuid(),
  })
  .strict() satisfies z.ZodType<RegulatoryEntityCounterpartyRef>;

export const CounterpartyRefSchema = z.discriminatedUnion('kind', [
  InternalRcScopeCounterpartyRefSchema,
  CompanyReceiverCounterpartyRefSchema,
  RegulatoryEntityCounterpartyRefSchema,
]) satisfies z.ZodType<CounterpartyRef>;

const validateInternalRcScopeConsistency = (
  value: {
    companyId: string;
    rcScopeType: string;
    rcScopeId: string;
    regulatoryEntityId?: string | null;
    counterpartyRef: {
      companyId: string;
      rcScopeType: string;
      rcScopeId: string;
      regulatoryEntityId?: string | null;
    };
  },
  ctx: z.RefinementCtx,
): void => {
  if (value.companyId !== value.counterpartyRef.companyId) {
    ctx.addIssue({
      code: 'custom',
      message: 'companyId must match counterpartyRef.companyId.',
      path: ['companyId'],
    });
  }
  if (value.rcScopeType !== value.counterpartyRef.rcScopeType) {
    ctx.addIssue({
      code: 'custom',
      message: 'rcScopeType must match counterpartyRef.rcScopeType.',
      path: ['rcScopeType'],
    });
  }
  if (value.rcScopeId !== value.counterpartyRef.rcScopeId) {
    ctx.addIssue({
      code: 'custom',
      message: 'rcScopeId must match counterpartyRef.rcScopeId.',
      path: ['rcScopeId'],
    });
  }
  if ((value.regulatoryEntityId ?? null) !== (value.counterpartyRef.regulatoryEntityId ?? null)) {
    ctx.addIssue({
      code: 'custom',
      message: 'regulatoryEntityId must match counterpartyRef.regulatoryEntityId.',
      path: ['regulatoryEntityId'],
    });
  }
};

const validateCompanyReceiverConsistency = (
  value: {
    companyReceiverId: string;
    regulatoryEntityId?: string | null;
    counterpartyRef: {
      companyReceiverId: string;
      regulatoryEntityId?: string | null;
    };
  },
  ctx: z.RefinementCtx,
): void => {
  if (value.companyReceiverId !== value.counterpartyRef.companyReceiverId) {
    ctx.addIssue({
      code: 'custom',
      message: 'companyReceiverId must match counterpartyRef.companyReceiverId.',
      path: ['companyReceiverId'],
    });
  }
  if ((value.regulatoryEntityId ?? null) !== (value.counterpartyRef.regulatoryEntityId ?? null)) {
    ctx.addIssue({
      code: 'custom',
      message: 'regulatoryEntityId must match counterpartyRef.regulatoryEntityId.',
      path: ['regulatoryEntityId'],
    });
  }
};

const validateRegulatoryEntityConsistency = (
  value: {
    regulatoryEntityId: string;
    counterpartyRef: {
      regulatoryEntityId: string;
    };
  },
  ctx: z.RefinementCtx,
): void => {
  if (value.regulatoryEntityId !== value.counterpartyRef.regulatoryEntityId) {
    ctx.addIssue({
      code: 'custom',
      message: 'regulatoryEntityId must match counterpartyRef.regulatoryEntityId.',
      path: ['regulatoryEntityId'],
    });
  }
};

export const InternalRcScopeCounterpartySnapshotSchema = z
  .object({
    kind: z.literal('INTERNAL_RC_SCOPE'),
    counterpartyRef: InternalRcScopeCounterpartyRefSchema,
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
    displayName: trimmedStringSchema,
    companyName: trimmedStringSchema.nullable(),
    storeName: trimmedStringSchema.nullable(),
    locationName: trimmedStringSchema.nullable(),
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict()
  .superRefine(validateInternalRcScopeConsistency);

export const CompanyReceiverCounterpartySnapshotSchema = z
  .object({
    kind: z.literal('COMPANY_RECEIVER'),
    counterpartyRef: CompanyReceiverCounterpartyRefSchema,
    companyReceiverId: z.uuid(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema,
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict()
  .superRefine(validateCompanyReceiverConsistency);

export const RegulatoryEntityCounterpartySnapshotSchema = z
  .object({
    kind: z.literal('REGULATORY_ENTITY'),
    counterpartyRef: RegulatoryEntityCounterpartyRefSchema,
    regulatoryEntityId: z.uuid(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict()
  .superRefine(validateRegulatoryEntityConsistency);

export const CounterpartySnapshotSchema = z.discriminatedUnion('kind', [
  InternalRcScopeCounterpartySnapshotSchema,
  CompanyReceiverCounterpartySnapshotSchema,
  RegulatoryEntityCounterpartySnapshotSchema,
]) satisfies z.ZodType<CounterpartySnapshot>;

export const InternalRcScopeCounterpartySelectionSchema = z
  .object({
    kind: z.literal('INTERNAL_RC_SCOPE'),
    counterpartyRef: InternalRcScopeCounterpartyRefSchema,
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
    displayName: trimmedStringSchema,
    companyName: trimmedStringSchema.nullable().optional(),
    storeName: trimmedStringSchema.nullable().optional(),
    locationName: trimmedStringSchema.nullable().optional(),
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict()
  .superRefine(validateInternalRcScopeConsistency);

export const CompanyReceiverCounterpartySelectionSchema = z
  .object({
    kind: z.literal('COMPANY_RECEIVER'),
    counterpartyRef: CompanyReceiverCounterpartyRefSchema,
    companyReceiverId: z.uuid(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema,
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict()
  .superRefine(validateCompanyReceiverConsistency);

export const RegulatoryEntityCounterpartySelectionSchema = z
  .object({
    kind: z.literal('REGULATORY_ENTITY'),
    counterpartyRef: RegulatoryEntityCounterpartyRefSchema,
    regulatoryEntityId: z.uuid(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict()
  .superRefine(validateRegulatoryEntityConsistency);

export const CounterpartySelectionSchema = z.discriminatedUnion('kind', [
  InternalRcScopeCounterpartySelectionSchema,
  CompanyReceiverCounterpartySelectionSchema,
  RegulatoryEntityCounterpartySelectionSchema,
]) satisfies z.ZodType<CounterpartySelection>;

export const CounterpartySearchSourceSchema = z.enum(
  counterpartySearchSourceValues,
) satisfies z.ZodType<CounterpartySearchSource>;

export const RegulatoryEntityVerificationStatusSchema = z.enum(
  regulatoryEntityVerificationStatusValues,
) satisfies z.ZodType<RegulatoryEntityVerificationStatus>;

export const RegulatoryEntityRecordSchema = z
  .object({
    id: z.uuid(),
    identity: RegulatoryIdentitySchema,
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    verificationStatus: RegulatoryEntityVerificationStatusSchema,
    addressLine1: trimmedStringSchema.nullable().optional(),
    addressLine2: trimmedStringSchema.nullable().optional(),
    city: trimmedStringSchema.nullable().optional(),
    state: trimmedStringSchema.nullable().optional(),
    postalCode: trimmedStringSchema.nullable().optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryEntityRecord>;
